import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { useI18n } from '../i18n';
import './ImageLightbox.css';

/**
 * ImageLightbox
 * A unified, reusable image viewer component.
 * Supports zoom (desktop/mobile), ESC to close, and overlay click to close.
 */
export default function ImageLightbox({ src, alt, onClose }) {
  const { t } = useI18n();
  const [zoom, setZoom] = useState(1);
  const prevZoomRef = useRef(1);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [constraints, setConstraints] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [pinchStartDist, setPinchStartDist] = useState(0);
  const [startZoom, setStartZoom] = useState(1);

  // Synchronize translation when zoom changes
  useEffect(() => {
    const oldZoom = prevZoomRef.current;
    const newZoom = zoom;

    if (newZoom !== oldZoom) {
      if (newZoom <= 1) {
        animate(x, 0, { type: 'spring', damping: 25, stiffness: 300 });
        animate(y, 0, { type: 'spring', damping: 25, stiffness: 300 });
      } else if (oldZoom > 1) {
        const ratio = (newZoom - 1) / (oldZoom - 1);
        x.set(x.get() * ratio);
        y.set(y.get() * ratio);
      }
      prevZoomRef.current = newZoom;
    }
  }, [zoom, x, y]);

  // Calculate drag constraints based on image scale and container size
  const updateConstraints = useCallback(() => {
    if (imgRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const baseWidth = imgRef.current.offsetWidth;
      const baseHeight = imgRef.current.offsetHeight;

      if (baseWidth === 0 || baseHeight === 0) return;

      const scaledWidth = baseWidth * zoom;
      const scaledHeight = baseHeight * zoom;

      // Calculate how much the image overflows the container
      const diffX = Math.max(0, scaledWidth - containerRect.width);
      const diffY = Math.max(0, scaledHeight - containerRect.height);

      // maxX/maxY is half the overflow because the image is centered
      const maxX = diffX / 2;
      const maxY = diffY / 2;

      // Clamping: when image is smaller than container, constraints should be 0 to keep it centered
      // When larger, diffX/2 allows panning to edges.
      // We reduce the "buffer" to avoid excessive dead zones.
      const bufferX = zoom > 1.1 ? 20 : 0;
      const bufferY = zoom > 1.1 ? 20 : 0;

      setConstraints({
        left: -(maxX + bufferX),
        right: maxX + bufferX,
        top: -(maxY + bufferY),
        bottom: maxY + bufferY
      });
    }
  }, [zoom]);

  // Update constraints on resize or when zoom changes
  useEffect(() => {
    updateConstraints();
    window.addEventListener('resize', updateConstraints);
    return () => window.removeEventListener('resize', updateConstraints);
  }, [updateConstraints]);

  // Close on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent scroll on body while lightbox is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.5, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
  }, []);

  const handleImageClick = (e) => {
    e.stopPropagation();
    // We only zoom on click if we weren't dragging
    if (!isDragging) {
      handleZoomIn();
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    handleZoomOut();
  };

  const handleWheel = (e) => {
    const delta = e.deltaY < 0 ? 0.2 : -0.2;
    setZoom((prev) => Math.min(Math.max(prev + delta, 1), 4));
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
      setPinchStartDist(dist);
      setStartZoom(zoom);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStartDist > 0) {
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
      const ratio = dist / pinchStartDist;
      setZoom(Math.min(Math.max(startZoom * ratio, 1), 4));
    }
  };

  const handleTouchEnd = () => {
    setPinchStartDist(0);
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="image-lightbox-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        onWheel={handleWheel}
      >
        <button
          className="image-lightbox-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close"
        >
          ✕
        </button>

        <div
          className="image-lightbox-viewport"
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <motion.img
            ref={imgRef}
            src={src}
            alt={alt || ''}
            className="image-lightbox-img"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: zoom, opacity: 1 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onLoad={updateConstraints}
            onClick={handleImageClick}
            onContextMenu={handleContextMenu}
            drag={zoom > 1 && pinchStartDist === 0}
            dragConstraints={constraints}
            dragElastic={0.05} // Minimal elastic feel, relying on our calculated buffer
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
            style={{
              x,
              y,
              scale: zoom,
              cursor: zoom > 1 ? 'grab' : 'zoom-in',
              touchAction: 'none',
              transformOrigin: 'center center'
            }}
          />
        </div>

        {zoom > 1 && (
          <div className="image-lightbox-hint">
            {zoom.toFixed(1)}x - {t('media.lightboxHint') || 'Click to zoom in | Right click to zoom out | Drag to pan'}
          </div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
